import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { FormInput } from '@catalyze/ui';
import { LoadingOverlay } from 'components/LoadingOverlay';
import type {
    ContactErrors,
    IContactForm,
} from 'features/SignUp/types/signUpForm';
import { STEP_COUNT } from 'features/SignUp/types/signUpForm';
import { emailSchema } from 'schemas/signUpForm';

import { FormLine, InputContainer } from './styled';

import { StyledForm } from '../../Form';
import { StyledFormButton } from '../../FormButton';
import { FormTitle } from '../../FormTitle';
import { ProgressBar } from '../../ProgressBar';

interface IEmailFormProps {
    initialValues: IContactForm | undefined;
    onSubmit: (data: IContactForm) => void;
    errors?: ContactErrors;
    loading?: boolean;
}

export const EmailForm: FC<IEmailFormProps> = ({
    onSubmit,
    initialValues,
    errors: propErrors,
    loading,
}) => {
    const formMethods = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(emailSchema),
    });

    const { handleSubmit, setError } = formMethods;

    useEffect(() => {
        if (propErrors) {
            (
                Object.entries(propErrors) as Array<
                    [keyof typeof propErrors, string]
                >
            ).forEach(([key, value], index) => {
                setError(key, { message: value }, { shouldFocus: index === 0 });
            });
        }
    }, [propErrors, setError]);

    return (
        <>
            <LoadingOverlay shown={loading} />
            <ProgressBar progress={(1 / STEP_COUNT) * 100} />
            <FormTitle
                title="Create an account"
                subtitle="Enter your email to start signing up."
            />
            <FormProvider {...formMethods}>
                <StyledForm onSubmit={handleSubmit(onSubmit)}>
                    <FormLine>
                        <InputContainer>
                            <FormInput
                                id="email"
                                type="email"
                                label="Email"
                                disabled={loading}
                            />
                        </InputContainer>
                    </FormLine>
                    <FormLine>
                        <InputContainer>
                            <FormInput
                                id="phone"
                                type="tel"
                                label="Phone"
                                placeholder="xxx-xxx-xxxx"
                                disabled={loading}
                            />
                        </InputContainer>
                    </FormLine>
                    <StyledFormButton type="submit" disabled={loading}>
                        Next
                    </StyledFormButton>
                </StyledForm>
            </FormProvider>
        </>
    );
};

EmailForm.defaultProps = {
    errors: undefined,
    loading: false,
};
